import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";

export default function SiteHeader({ showNav = true }) {
  const [open, setOpen] = useState(false);
  const [openSubKey, setOpenSubKey] = useState(null); // mobile/expanded sub menu
  const [hoverKey, setHoverKey] = useState(null); // desktop hover dropdown
  const closeTimer = useRef(null);
  const { t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setOpenSubKey(null);
    setHoverKey(null);
  }, [location.pathname, location.hash]);

  // Build menu with optional sub-items (anchors to Section ids on the target page)
  const items = [
    { to: "/editorial", label: t("menuEditorial"), testId: "nav-editorial" },
    {
      to: "/raus",
      label: t("menuRaus"),
      testId: "nav-raus",
      subs: [
        { to: "/raus#section-idee-ursprung", label: t("raus_11_heading") },
        { to: "/raus#section-weg", label: t("raus_12_heading") },
        { to: "/raus#section-gemaelde", label: t("raus_13_heading") },
      ],
    },
    {
      to: "/song",
      label: t("menuSong"),
      testId: "nav-song",
      subs: [
        { to: "/song#section-anhoeren", label: t("song_21_heading") },
        { to: "/song#section-bedeutung", label: t("song_22_heading") },
        { to: "/song#section-text-story", label: t("song_23_heading") },
        { to: "/song#section-download", label: t("song_24_heading") },
      ],
    },
    {
      to: "/social-seller",
      label: t("menuSocialSeller"),
      testId: "nav-social-seller",
      subs: [
        { to: "/social-seller#section-was-ist", label: t("ss_31_heading") },
        { to: "/social-seller#section-warum-heute", label: t("ss_32_heading") },
        { to: "/social-seller#section-wichtigster-satz", label: t("ss_33_heading") },
        { to: "/social-seller#section-nicht-ist", label: t("ss_34_heading") },
        { to: "/social-seller#section-was-brauchst", label: t("ss_35_heading") },
        { to: "/social-seller#section-erster-schritt", label: t("ss_36_heading") },
      ],
    },
    { to: "/inspiration", label: t("menuInspiration"), testId: "nav-inspiration" },
    { to: "/stimmen", label: t("menuStimmen"), testId: "nav-stimmen" },
    { to: "/downloads", label: t("menuDownloads"), testId: "nav-downloads" },
  ];

  const openSub = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setHoverKey(key);
  };
  const scheduleCloseSub = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setHoverKey(null), 150);
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-30 w-full"
      style={{ backgroundColor: "#2A2624" }}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-6 py-4 sm:px-10 sm:py-5 lg:px-12">
        <Link
          to="/"
          data-testid="site-header-home-link"
          className="flex min-w-0 shrink items-center transition-opacity duration-200 hover:opacity-80"
          aria-label={t("logoTitle")}
        >
          <div className="flex flex-col leading-tight">
            <span
              data-testid="site-header-logo-title"
              className="block truncate text-white"
              style={{
                fontWeight: 700,
                fontSize: "clamp(12px, 1.6vw, 16px)",
                letterSpacing: "0.4px",
                lineHeight: 1.05,
              }}
            >
              {t("logoTitle")}
            </span>
            <span
              data-testid="site-header-logo-subtitle"
              className="block truncate text-white/70"
              style={{
                fontWeight: 400,
                fontSize: "clamp(8px, 1vw, 10px)",
                letterSpacing: "1.2px",
                textTransform: "uppercase",
                marginTop: "2px",
              }}
            >
              {t("logoSubtitle")}
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        {showNav && (
          <nav className="hidden items-center gap-6 lg:flex">
            {items.map((it) => {
              const hasSubs = it.subs && it.subs.length > 0;
              return (
                <div
                  key={it.to}
                  className="relative"
                  onMouseEnter={() => hasSubs && openSub(it.to)}
                  onMouseLeave={() => hasSubs && scheduleCloseSub()}
                >
                  <NavLink
                    to={it.to}
                    data-testid={it.testId}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1 whitespace-nowrap text-[14px] text-white transition-colors duration-200 hover:text-white/70 ${
                        isActive
                          ? "border-b border-white/80 pb-0.5"
                          : "border-b border-transparent pb-0.5"
                      }`
                    }
                    style={{ letterSpacing: "0.3px", fontWeight: 500 }}
                  >
                    {it.label}
                    {hasSubs ? (
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform duration-200 ${
                          hoverKey === it.to ? "rotate-180" : ""
                        }`}
                      />
                    ) : null}
                  </NavLink>

                  {hasSubs && hoverKey === it.to ? (
                    <div
                      data-testid={`${it.testId}-dropdown`}
                      onMouseEnter={() => openSub(it.to)}
                      onMouseLeave={scheduleCloseSub}
                      className="absolute left-0 top-full z-40 mt-2 min-w-[260px] rounded-md py-2 shadow-xl"
                      style={{
                        backgroundColor: "#2A2624",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    >
                      {it.subs.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          data-testid={`${it.testId}-sub-${s.to.split("#")[1]}`}
                          className="block px-4 py-2.5 text-[13px] text-white/85 transition-colors duration-150 hover:bg-white/5 hover:text-white"
                          style={{ letterSpacing: "0.2px", fontWeight: 400 }}
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </nav>
        )}

        <div className="flex shrink-0 items-center gap-4">
          {showNav && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              className="text-white lg:hidden"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile nav drawer */}
      {showNav && open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden"
          style={{ backgroundColor: "#2A2624", borderTop: "1px solid rgba(255,255,255,0.08)" }}
        >
          <nav className="flex flex-col px-6 pb-6 pt-2 sm:px-10">
            {items.map((it) => {
              const hasSubs = it.subs && it.subs.length > 0;
              const isSubOpen = openSubKey === it.to;
              return (
                <div key={it.to} className="border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <NavLink
                      to={it.to}
                      data-testid={`${it.testId}-mobile`}
                      className={({ isActive }) =>
                        `flex-1 py-4 text-base text-white transition-colors duration-200 hover:text-white/70 ${
                          isActive ? "text-white" : "text-white/90"
                        }`
                      }
                      style={{ letterSpacing: "0.3px", fontWeight: 500 }}
                    >
                      {it.label}
                    </NavLink>
                    {hasSubs ? (
                      <button
                        type="button"
                        onClick={() => setOpenSubKey(isSubOpen ? null : it.to)}
                        data-testid={`${it.testId}-mobile-toggle`}
                        aria-label="Untermenü"
                        className="px-2 py-4 text-white/70 transition-colors duration-200 hover:text-white"
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            isSubOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    ) : null}
                  </div>
                  {hasSubs && isSubOpen ? (
                    <div className="flex flex-col pb-3 pl-4">
                      {it.subs.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          data-testid={`${it.testId}-mobile-sub-${s.to.split("#")[1]}`}
                          className="py-2 text-[13px] text-white/80 transition-colors duration-150 hover:text-white"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
